import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { BrickSettingsService, WebViewerBrickSettingRemoteDto } from 'src/generated/swagger';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import { ChangeAllowAttributes, ChangeTagDrivenURL, ChangeUseSkillURL } from './webviewer-settings.actions';

export interface WebViewerSettingsStateModel extends IBrickSettingStateModel<WebViewerBrickSettingRemoteDto> {}
function getDefaultSpecificBrickSetting(): WebViewerBrickSettingRemoteDto {
   return {
      tagDrivenURL: '',
      allowAttributes: '',
      useSkillURL: false,
      commonBrickSettingRemoteDto: null,
   };
}

@Injectable()
@State<WebViewerSettingsStateModel>({
   name: 'webviewerBrickSetting',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
   },
})
export class WebViewerSettingState extends SpecificBrickSettingsStateBase<WebViewerBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([WebViewerSettingState, WebViewerSettingState.getSettingModelFn])
   public static getUseSkillURL(state: WebViewerSettingsStateModel, getSettingModelFn: (state, brickInstanceId) => WebViewerBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).useSkillURL;
      };
   }

   @Selector([WebViewerSettingState, WebViewerSettingState.getSettingModelFn])
   public static getTagDrivenURLSetting(state: WebViewerSettingsStateModel, getSettingModelFn: (state, brickInstanceId) => WebViewerBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).tagDrivenURL;
      };
   }

   @Selector([WebViewerSettingState, WebViewerSettingState.getSettingModelFn])
   public static getAllowAttributes(state: WebViewerSettingsStateModel, getSettingModelFn: (state, brickInstanceId) => WebViewerBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).allowAttributes;
      };
   }

   @Action(ChangeUseSkillURL)
   @ImmutableContext()
   public useSkillURL(ctx: StateContext<WebViewerSettingsStateModel>, action: ChangeUseSkillURL) {
      ctx.setState((state: WebViewerSettingsStateModel) => {
         const brickSettingModel: WebViewerBrickSettingRemoteDto = WebViewerSettingState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.useSkillURL = action.useSkillURL;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeTagDrivenURL)
   @ImmutableContext()
   public changeTagDrivenURL(ctx: StateContext<WebViewerSettingsStateModel>, action: ChangeTagDrivenURL) {
      ctx.setState((state: WebViewerSettingsStateModel) => {
         const brickSettingModel: WebViewerBrickSettingRemoteDto = WebViewerSettingState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.tagDrivenURL = action.tagDrivenURL;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeAllowAttributes)
   @ImmutableContext()
   public changeAllowAttributes(ctx: StateContext<WebViewerSettingsStateModel>, action: ChangeAllowAttributes) {
      ctx.setState((state: WebViewerSettingsStateModel) => {
         const brickSettingModel: WebViewerBrickSettingRemoteDto = WebViewerSettingState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.allowAttributes = action.allowAttributes;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: WebViewerBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeWebViewerBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }
}
